import { useEffect, useState } from 'react'
import { create } from 'zustand'
import { onlineManager, type QueryClient } from '@tanstack/react-query'
import { del, get, set } from 'idb-keyval'
import { OfflineError, PHOTO_CACHE_MAX, type EquipmentItem } from '@mads/shared'
import { api } from './session'
import { queryKeys } from './queries'
import { useQueueStore } from './queue'
import { hasPhoto, prunePhotos, putPhoto } from './photoCache'

/**
 * Bringing the whole inventory down to the device, on a schedule (issue #70).
 *
 * Issue #64 gave the app a persisted read cache, and that cache is a
 * dehydration of what was *actually fetched* — so what a walker had underground
 * was the screens they happened to have opened, not the inventory. Never opened
 * the stocktakes screen and there was no walk to start in the cellar; never
 * scrolled the fixture models and there were no optics. The promise the Offline
 * chip makes is bigger than that, and this is what makes it true.
 *
 * **No new endpoint and no delta protocol.** It fetches the same queries the
 * screens read, into the same keys, so `InventoryPresenter` stays the only thing
 * that decides the shape of a record and there is no second wire format to keep
 * in step. A `?since=` cursor would be a second thing to get right for a payload
 * measured in hundreds of kilobytes — the whole live inventory is one
 * unpaginated `GET`, which is what `EquipmentController::list()` already
 * answers.
 */

/**
 * How often the device brings itself up to date while the app is open.
 *
 * Frequent enough that somebody who walks away from a desk and downstairs half
 * an hour later has something recent, rare enough that a tablet left open on a
 * bench all week is not asking for the whole inventory every minute. The reads
 * are cheap and cached; what this really bounds is how stale the *cellar* copy
 * can be while the app was demonstrably able to refresh it.
 */
export const SNAPSHOT_INTERVAL = 15 * 60 * 1000

/**
 * When a saved copy is old enough to say so out loud.
 *
 * Three intervals rather than one: a single missed refresh is a tab that was
 * backgrounded or a wifi blip, and a chip that appears for that is furniture.
 * Three in a row means the device has been failing to reach the API for
 * three quarters of an hour while believing it was online, which is worth
 * knowing *before* going downstairs.
 */
export const SNAPSHOT_STALE_AFTER = SNAPSHOT_INTERVAL * 3

const STORAGE_KEY = 'mads.snapshot'

interface SnapshotState {
  /** When the last full refresh finished, or null if none ever has. */
  at: number | null
  running: boolean
  /**
   * The last refresh failed. Not surfaced as an error anybody must dismiss —
   * a failed snapshot costs nothing that is not already on the device, and the
   * age is the honest thing to show instead.
   */
  failed: boolean
}

interface SnapshotActions {
  /** Reads back when this device was last brought up to date. */
  load: () => Promise<void>
  refresh: (queryClient: QueryClient) => Promise<void>
  /** Sign-out. The copy is being forgotten, so its date goes with it. */
  forget: () => Promise<void>
}

export const useSnapshotStore = create<SnapshotState & SnapshotActions>()((setState, getState) => ({
  at: null,
  running: false,
  failed: false,

  load: async () => {
    const stored = await get<number>(STORAGE_KEY)

    // Only ever fills a gap. A refresh that happened in this session is more
    // recent than anything on disk, and a load racing it must not wind the
    // clock back.
    if (getState().at === null && typeof stored === 'number') setState({ at: stored })
  },

  refresh: async (queryClient) => {
    if (!canRefresh(getState())) return

    setState({ running: true })

    try {
      await fetchEverything(queryClient)

      const at = Date.now()

      setState({ at, running: false, failed: false })
      await set(STORAGE_KEY, at)
    } catch {
      // Nothing is lost by a failed refresh — the device still holds whatever it
      // held before, which is the entire point of having it. The age is what
      // says so, and it simply stops moving.
      setState({ running: false, failed: true })
    }
  },

  forget: async () => {
    setState({ at: null, running: false, failed: false })
    await del(STORAGE_KEY)
  },
}))

/**
 * Whether now is a moment to bring the device up to date.
 *
 * **Not while the queue has anything in it**, which is the one that is easy to
 * get wrong. The caches at that moment hold the walker's own unsent changes
 * patched on top of the server's copy (`offlineWrites.ts`), and a refresh would
 * replace them with the server's version — the walker's work vanishing off the
 * screen while it sits perfectly safe in IndexedDB. The queue is replayed first
 * and `useSyncStore.run()` invalidates everything on its way out, which *is* the
 * refresh; there is no second implementation of "apply the queue to a cache" and
 * there should not be one.
 */
function canRefresh(state: SnapshotState): boolean {
  return (
    !state.running && onlineManager.isOnline() && useQueueStore.getState().changesets.length === 0
  )
}

/**
 * Kit and places, which is what a walk needs — and exactly the set
 * `lib/offlineCache.ts` is willing to persist. Anything fetched here that the
 * allowlist did not name would be pulled down every quarter of an hour and
 * dropped again on the next write to disk, so the two lists have to agree.
 */
async function fetchEverything(queryClient: QueryClient): Promise<void> {
  const [items, , models] = await Promise.all([
    queryClient.fetchQuery({ queryKey: queryKeys.items, queryFn: () => api.items() }),
    queryClient.fetchQuery({ queryKey: queryKeys.locations, queryFn: () => api.locations() }),
    queryClient.fetchQuery({
      queryKey: queryKeys.fixtureModels,
      queryFn: () => api.fixtureModels(),
    }),
    queryClient.fetchQuery({ queryKey: queryKeys.autocomplete, queryFn: () => api.autocomplete() }),
    queryClient.fetchQuery({ queryKey: queryKeys.dashboard, queryFn: () => api.dashboard() }),
    fetchWalk(queryClient),
  ])

  // After the records, because the records are what say which photographs there
  // are — and outside the `Promise.all`, because the JSON is what a walk cannot
  // do without and the pictures are what make it pleasant.
  await fetchPhotographs([...items, ...models])
}

/**
 * Brings the photographs down too (issue #70).
 *
 * Newest first and bounded at `PHOTO_CACHE_MAX`: kit somebody has touched
 * lately is what somebody is most likely to be looking at, and a store with
 * more photographs than the bound sheds the oldest rather than filling a
 * phone's disk.
 *
 * **Failures here never fail the snapshot.** A missing photograph is a gap that
 * fills in next time; a missing inventory is a walk that cannot happen. The one
 * thing worth reacting to is the connection going, and then the rest are
 * abandoned rather than attempted — they are all coming from the same bucket
 * through the same API, so the second would fail for the reason the first did.
 * That is the same rule the queue's uploads follow on the way out.
 */
async function fetchPhotographs(records: readonly PhotographedRecord[]): Promise<void> {
  const wanted = records
    .filter((record) => record.image != null && record.image.pending !== true)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .slice(0, PHOTO_CACHE_MAX)
    .map((record) => versionedPhotoUrl(record))

  for (const url of wanted) {
    if (await hasPhoto(url)) continue

    try {
      await putPhoto(url, await api.imageBlob(url))
    } catch (error) {
      if (error instanceof OfflineError) break
    }
  }

  await prunePhotos(new Set(wanted))
}

type PhotographedRecord = Pick<EquipmentItem, 'image' | 'updatedAt'>

/**
 * The exact string `RecordPhoto` asks for, so what is cached here is what is
 * read back there. The path is stable by design (issue #56), so the version is
 * the only thing that makes a replaced photograph arrive.
 */
function versionedPhotoUrl(record: PhotographedRecord): string {
  return `${record.image?.url ?? ''}?v=${encodeURIComponent(record.updatedAt)}`
}

/**
 * The stocktake list, and the open walk's own detail behind it.
 *
 * The detail is the half that matters underground: the list says a walk is open,
 * and the lines — every item expected in every location, in walking order — are
 * what somebody actually counts against. A walk started at a desk and continued
 * in a cellar needs both, and only one of them is in the list.
 */
async function fetchWalk(queryClient: QueryClient): Promise<void> {
  const stocktakes = await queryClient.fetchQuery({
    queryKey: queryKeys.stocktakes,
    queryFn: () => api.stocktakes(),
  })

  const open = stocktakes.open

  // A provisional walk started offline has a negative id and nothing on the
  // server to fetch under it, exactly as `useStocktake` guards for.
  if (open === null || open.id <= 0) return

  await queryClient.fetchQuery({
    queryKey: queryKeys.stocktake(open.id),
    queryFn: () => api.stocktake(open.id),
  })
}

/**
 * How old the device's copy is, in milliseconds, or null if there is none.
 *
 * It ticks on its own, because the thing it feeds is a sentence about elapsed
 * time on a screen somebody may be staring at for an hour without touching. A
 * minute is fine grain enough for "saved 4 minutes ago" and cheap enough not to
 * think about.
 */
export function useSnapshotAge(): number | null {
  const at = useSnapshotStore((s) => s.at)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60_000)

    return () => clearInterval(timer)
  }, [])

  return at === null ? null : Math.max(0, now - at)
}

/**
 * How long ago, in words — the same register `describeDays` uses, because this
 * is read by somebody deciding whether to walk downstairs rather than by
 * somebody reading a log.
 */
export function describeAge(ms: number): string {
  const minutes = Math.floor(ms / 60_000)

  if (minutes < 1) return 'just now'
  if (minutes === 1) return 'a minute ago'
  if (minutes < 60) return `${minutes} minutes ago`

  const hours = Math.round(minutes / 60)

  if (hours === 1) return 'an hour ago'
  if (hours < 24) return `${hours} hours ago`

  const days = Math.round(hours / 24)

  return days === 1 ? 'yesterday' : `${days} days ago`
}
